<template>
  <section class="orders-terminal box-rounded">
    <header class="ot-header">
      <h3>Terminal BTCUSDT</h3>
      <span class="ot-account">Mexc · MatheusInvestidor</span>
      <span class="ot-balances">BTC livre {{ formatAsset(terminalBalances.BTC, 8) }} · USDT livre {{ formatAsset(terminalBalances.USDT, 2) }}</span>
      <button class="ot-btn" type="button" @click="fetchOrders">Atualizar</button>
    </header>

    <p v-if="terminalError" class="ot-error" role="alert">{{ terminalError }}</p>
    <p v-if="balanceHint" class="ot-hint">{{ balanceHint }}</p>

    <form class="ot-form" @submit.prevent="createOrder">
      <select v-model="form.side" class="ot-input">
        <option value="BUY">BUY</option>
        <option value="SELL">SELL</option>
      </select>
      <input v-model.number="form.price" class="ot-input" type="number" min="0" step="0.01" placeholder="Preço" required />
      <input v-model.number="form.qty" class="ot-input" type="number" min="0" step="0.000001" placeholder="Quantidade" required />
      <button class="ot-btn primary" type="submit" :disabled="!canCreate">Colocar limite</button>
    </form>

    <div class="ot-grid">
      <div class="ot-col">
        <h4>Limites abertas <span class="ot-count">{{ openOrders.length }}</span></h4>
        <p v-if="openOrders.length === 0" class="ot-empty">Nenhuma limite resting em BTCUSDT.</p>
        <article
          v-for="order in openOrders"
          :key="order.orderId"
          :class="['ot-card', order.side === 'BUY' ? 'buy' : 'sell']"
        >
          <div class="ot-row">
            <strong>{{ order.side }}</strong>
            <span>{{ formatPrice(order.price) }}</span>
            <span class="ot-status">{{ order.status }}</span>
          </div>
          <div class="ot-meta">
            <span>Orig {{ order.origQty }}</span>
            <span>Restante {{ order.remainQty }}</span>
            <span>Exec {{ order.executedQty }}</span>
          </div>
          <div class="ot-meta">{{ order.time }} · {{ order.orderId }}</div>

          <form v-if="editingId === order.orderId" class="ot-edit" @submit.prevent="saveEdit(order)">
            <input v-model.number="editPrice" class="ot-input" type="number" min="0" step="0.01" required />
            <input v-model.number="editQty" class="ot-input" type="number" min="0" step="0.000001" required />
            <button class="ot-btn primary" type="submit" :disabled="!canEdit">Salvar</button>
            <button class="ot-btn" type="button" @click="editingId = null">Fechar</button>
          </form>
          <div v-else class="ot-actions">
            <button class="ot-btn" type="button" @click="startEdit(order)">Editar</button>
            <button class="ot-btn danger" type="button" @click="cancelOrder(order)">Cancelar</button>
          </div>
        </article>
      </div>

      <div class="ot-col">
        <h4>Execuções <span class="ot-count">{{ cards.length }}</span></h4>
        <p v-if="cards.length === 0" class="ot-empty">Nenhum fill de BTCUSDT desde que o processo subiu.</p>
        <article
          v-for="card in cards"
          :key="card.orderId"
          :class="['ot-card', card.side === 'BUY' ? 'buy' : 'sell']"
        >
          <div class="ot-row">
            <strong>{{ card.side }}</strong>
            <span>{{ formatPrice(card.execPrice) }}</span>
            <span class="ot-status">{{ card.status }}</span>
          </div>
          <div class="ot-meta">
            <span>Qty {{ card.execQty }}</span>
            <span>{{ card.fills.length }} trade(s)</span>
          </div>
          <div class="ot-meta">{{ card.time }} · {{ card.orderId }}</div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'OrdersTerminal',

  data() {
    return {
      form: { side: 'BUY', price: null, qty: null },
      editingId: null,
      editPrice: null,
      editQty: null,
    }
  },

  computed: {
    ...mapState(['terminalOpenOrders', 'terminalCards', 'terminalBalances', 'terminalError']),

    openOrders() {
      return [...(this.terminalOpenOrders || [])].sort((a, b) => (a.price || 0) - (b.price || 0))
    },

    cards() {
      return [...(this.terminalCards || [])]
    },

    canCreate() {
      return this.form.side && Number(this.form.price) > 0 && Number(this.form.qty) > 0
    },

    canEdit() {
      return Number(this.editPrice) > 0 && Number(this.editQty) > 0
    },

    balanceHint() {
      const balances = this.terminalBalances || {}
      const price = Number(this.form.price)
      const qty = Number(this.form.qty)
      if (this.form.side === 'BUY' && price > 0 && qty > 0 && balances.USDT != null) {
        const cost = price * qty
        if (cost > Number(balances.USDT)) {
          return `USDT livre insuficiente para esta compra (${this.formatAsset(cost, 2)} necessário).`
        }
      }
      if (this.form.side === 'SELL' && qty > 0 && balances.BTC != null && qty > Number(balances.BTC)) {
        return 'BTC livre insuficiente para esta venda.'
      }
      return ''
    },
  },

  watch: {
    '$store.state.socket'(socket) {
      if (socket && socket.readyState === WebSocket.OPEN) this.fetchOrders()
    },
  },

  mounted() {
    this.fetchOrders()
  },

  methods: {
    formatPrice(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatAsset(value, digits) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('pt-BR', { minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits })
    },

    send(payload) {
      const socket = this.$store.state.socket
      if (!socket || socket.readyState !== WebSocket.OPEN) return
      socket.send(JSON.stringify(payload))
    },

    fetchOrders() {
      this.send({ type: 'terminalFetchOrders' })
    },

    createOrder() {
      if (!this.canCreate) return
      this.send({
        type: 'terminalCreateOrder',
        side: this.form.side,
        price: this.form.price,
        qty: this.form.qty,
      })
      this.form.price = null
      this.form.qty = null
    },

    startEdit(order) {
      this.editingId = order.orderId
      this.editPrice = order.price
      this.editQty = order.remainQty || order.origQty
    },

    saveEdit(order) {
      if (!this.canEdit) return
      this.send({
        type: 'terminalEditOrder',
        orderId: order.orderId,
        price: this.editPrice,
        qty: this.editQty,
      })
      this.editingId = null
    },

    cancelOrder(order) {
      this.send({ type: 'terminalCancelOrder', orderId: order.orderId })
      if (this.editingId === order.orderId) this.editingId = null
    },
  },
}
</script>

<style scoped>
.orders-terminal {
  margin-bottom: 20px;
  padding: 16px;
}

.ot-header,
.ot-form,
.ot-row,
.ot-meta,
.ot-actions,
.ot-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ot-header {
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.ot-header h3,
.ot-col h4 {
  margin: 0;
}

.ot-account,
.ot-meta,
.ot-empty,
.ot-status {
  color: var(--text-muted, #9ca3af);
  font-size: 12px;
}

.ot-form {
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.ot-balances {
  color: var(--text-color);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.ot-error,
.ot-hint {
  margin: 0 0 12px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
}

.ot-error {
  border: 1px solid #ef4444;
  background: rgba(239, 68, 68, 0.12);
  color: #fca5a5;
}

.ot-hint {
  border: 1px solid #f59e0b;
  background: rgba(245, 158, 11, 0.12);
  color: #fcd34d;
}

.ot-input {
  border: 1px solid var(--table-row-border, #3a3a3a);
  border-radius: 6px;
  background: transparent;
  color: var(--text-color);
  padding: 6px 8px;
}

.ot-btn {
  border: 1px solid #4b5563;
  border-radius: 6px;
  background: transparent;
  color: var(--text-color);
  padding: 6px 10px;
  cursor: pointer;
}

.ot-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.ot-btn.primary {
  border-color: #6366f1;
  color: #a5b4fc;
}

.ot-btn.danger {
  border-color: #ef4444;
  color: #fca5a5;
}

.ot-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.ot-col {
  min-height: 180px;
}

.ot-count {
  color: var(--text-muted, #9ca3af);
  font-weight: 500;
}

.ot-card {
  background: var(--table-row-bgcolor);
  border-radius: 8px;
  border-left: 4px solid #6b7280;
  padding: 10px 12px;
  margin-top: 8px;
}

.ot-card.buy { border-left-color: #10b981; }
.ot-card.sell { border-left-color: #ef4444; }

.ot-row {
  justify-content: space-between;
  font-size: 13px;
}

.ot-meta {
  margin-top: 4px;
  flex-wrap: wrap;
}

.ot-actions,
.ot-edit {
  margin-top: 8px;
  flex-wrap: wrap;
}

@media (max-width: 900px) {
  .ot-grid {
    grid-template-columns: 1fr;
  }
}
</style>
